import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Plus, UserRound, X } from 'lucide-react';
import { packingApi } from '../api/packing';
import { ItemBadge, UnitOwnedToggle } from '../ui/ItemBadge';
import { gearApi } from '../api/gear';
import type { PackingItem, PackingList, PackingOffer, PackingProgress } from '../api/packing';
import { subgroupsApi } from '../api/subgroups';
import type { Subgroup } from '../api/subgroups';
import { rosterApi } from '../api/auth';
import type { RosterRow } from '../api/auth';
import { Button } from '../ui/Button';
import { TextField } from '../ui/Field';
import { PanelTabs } from '../ui/Tabs';

/**
 * What to bring, on the event page where the rest of the answers live.
 *
 * **The words are deliberately not "packing".** A campout is packed for; a Monday meeting is not,
 * and somebody bringing snacks to one is doing the same thing this feature is for. The entity is
 * still `PackingList` — §8's name, and the design of record's — but every word a family reads says
 * what it actually means, which is what a unit running mostly meetings needs it to say.
 *
 * Three kinds of list sit here and they are read very differently. **What the unit and each group
 * bring** is a public question — who has the griddle — and the whole point is that it is answered
 * as state on the event rather than in 200 messages the night before. **What one person brings** is
 * theirs and their guardians', and a leader sees a count and never the contents; that is why the
 * leader's line below comes from its own endpoint rather than from anything rendered here.
 *
 * Nothing exists for a Scout until they open it. The first-run choice — the unit's list, one of
 * mine, or blank — is offered at the moment they look, which is the natural one, rather than on a
 * settings screen somebody has to find (§8, N2).
 *
 * **The three levels do not share a card.** A Personal-level list published onto an event is the
 * *suggestion* every Scout copies from, and it used to render through `SharedList` as well as
 * through `MyKit`, so it appeared twice — once subtitled "what the group brings", which it is not,
 * and once with an assignee dropdown per item, which the API refuses outright for personal gear
 * (TROOP-74). `SharedList` takes Unit and Subgroup lists only, and what it renders is stated by
 * the caller rather than inferred from the level.
 */

/**
 * The three levels in words rather than in the enum's own vocabulary. "Personal" and "Subgroup" are
 * model words; what a family reads is who is responsible for the thing — and for the middle one,
 * the unit's own word for a group, never a literal (rule 16).
 */
function levelWords(level: string, subgroupLabelSingular: string): string {
  switch (level) {
    case 'Personal':
      return 'what each person brings';
    case 'Subgroup':
      return `what each ${subgroupLabelSingular.toLowerCase()} brings`;
    default:
      return 'what the unit brings';
  }
}

export function EventPacking({
  unitId,
  eventId,
  meId,
  canManage,
  subgroupLabelSingular,
  subgroupLabelPlural,
  onChanged,
}: {
  unitId: string;
  eventId: string;
  /** Whose page this is. What makes "down to you" answerable without a second request. */
  meId: string;
  /** Officer and above. The API is the enforcement point; this decides what renders. */
  canManage: boolean;
  /** The unit's own word for a group — Patrol, Den, Crew. Rendered, never assumed (rule 16). */
  subgroupLabelSingular: string;
  subgroupLabelPlural: string;
  /** Tells the docket row above to re-read its counts (TROOP-116). */
  onChanged?: () => void;
}) {
  const [lists, setLists] = useState<PackingList[] | null>(null);
  const [mine, setMine] = useState<PackingList | null>(null);
  const [progress, setProgress] = useState<PackingProgress | null>(null);
  const [templates, setTemplates] = useState<PackingList[] | null>(null);
  const [offers, setOffers] = useState<PackingOffer[] | null>(null);
  const [roster, setRoster] = useState<RosterRow[]>([]);
  const [subgroups, setSubgroups] = useState<Subgroup[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Which half is on screen. Yours by default and for everybody, officer included: the
  // question almost every reader has is what goes in their car, and the planning behind it is
  // a thing you go and look at rather than the thing you land on.
  const [tab, setTab] = useState('yours');

  const load = useCallback(async () => {
    try {
      const [onEvent, own] = await Promise.all([
        packingApi.forEvent(unitId, eventId),
        packingApi.myList(unitId, eventId),
      ]);

      setLists(onEvent);
      setMine(own ?? null);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load what to bring.');
    }
  }, [unitId, eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    // Who the groups are, so each one's list can say whose it is and offer that group's own
    // people. Quiet on failure: it collapses the breakout, it does not break the section.
    subgroupsApi
      .list(unitId)
      .then((all) => setSubgroups(all.filter((s) => !s.isRetired)))
      .catch(() => undefined);
  }, [unitId]);

  useEffect(() => {
    if (!canManage) return;

    // Officer-only reads, and all of them fail quietly: none is the reason anybody opened this
    // page, and a red banner over the packing section because a count did not load would be worse
    // than the count being absent.
    packingApi.progress(unitId, eventId).then(setProgress).catch(() => undefined);
    packingApi.templates(unitId).then(setTemplates).catch(() => undefined);
    packingApi.offers(unitId, eventId).then(setOffers).catch(() => undefined);
    rosterApi.list(unitId).then(setRoster).catch(() => undefined);
  }, [unitId, eventId, canManage]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);

    try {
      await action();
      await load();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  const all = useMemo(() => lists ?? [], [lists]);

  // Everything the event publishes, at any level. This is what stops a template being published
  // twice, so it is deliberately the whole set rather than the subset rendered as cards below.
  const published = all.filter((l) => l.forPersonId === null);

  const unitLists = published.filter((l) => l.level === 'Unit');

  // A suggestion carries no subgroup; each group's working copy names the group it belongs to,
  // which is what makes the breakout possible at all. Plural on purpose: nothing stops an officer
  // publishing two group-level lists onto one event, and a `.find` here would silently hide the
  // second — which is what the old flat rendering did get right.
  const groupSuggestions = published.filter((l) => l.level === 'Subgroup' && l.subgroupId === null);
  const groupCopies = all.filter((l) => l.level === 'Subgroup' && l.subgroupId !== null);

  // The kit every Scout copies from. It feeds MyKit below; it is not a card of its own except for
  // the officer who curates it, and it never carries an assignee. Plural for the same reason the
  // group ones are: two can be published, and only one of them may be hidden by accident.
  const personalSuggestions = published.filter((l) => l.level === 'Personal');

  const rosterPeople = roster
    .filter((r) => r.permissionLevel !== 'None')
    .map((r) => ({ personId: r.personId, displayName: r.displayName }));

  // What this person is on the hook for, read off the lists the server already returned. No second
  // request, and no copy into their own list: the assignment lives on the shared list, so a copy
  // would go stale the moment an officer moved the stove to somebody else.
  const assignedFrom = (level: string): Assignment[] =>
    published
      .filter((l) => l.level === level)
      .flatMap((l) =>
        l.items
          .filter((i) => i.assignedToPersonId === meId)
          .map((item) => ({
            listId: l.id,
            listName: l.name,
            groupName: subgroups.find((s) => s.id === l.subgroupId)?.name ?? null,
            item,
          })),
      );

  const troopAssignments = assignedFrom('Unit');
  const groupAssignments = assignedFrom('Subgroup');

  // Only the groups that actually have a list get a card. The ones that do not are a picker, not
  // a row each: a unit with twenty patrols would otherwise scroll past twenty "has not started"
  // lines to reach the list it came for, and the dev database (eighty-two groups of smoke
  // residue) renders that mistake at a scale that makes it obvious.
  const groupsWithLists = subgroups.filter((s) =>
    groupCopies.some((l) => l.subgroupId === s.id),
  );
  const groupsWithout = subgroups.filter(
    (s) => !groupCopies.some((l) => l.subgroupId === s.id),
  );

  // The plan behind the checklist: what the unit and each group are bringing, who has each thing,
  // and — for an officer — the controls that put lists on the event in the first place. A member
  // reads it and writes nothing, which is the policy's own position: "a member has to see the list
  // to pack from it, and to see who is bringing what". So this is not a manage-only tab, and the
  // API is what withholds the controls rather than this component (TROOP-76).
  const plan = (
    <>
      {/* The leader's whole view of what individuals are bringing: how many are ready, and nothing
          about what any one of them has on their list. The signal without the surveillance (§8). */}
      {canManage && progress && (
        <p
          className="t-caption tabular"
          style={{ color: 'var(--color-text-secondary)', margin: '0 0 var(--space-4)' }}
        >
          {progress.finished} of {progress.expected}{' '}
          {progress.expected === 1 ? 'Scout has' : 'Scouts have'} everything on their list
          {progress.started > progress.finished &&
            ` · ${progress.started - progress.finished} part way`}
          . You see the count, not what anybody is bringing.
        </p>
      )}

      {unitLists.map((list) => (
        <SharedList
          key={list.id}
          list={list}
          unitId={unitId}
          subtitle="What the unit brings"
          people={rosterPeople}
          canManage={canManage}
          busy={busy}
          onRun={run}
        />
      ))}

      {/* Every group packs its own gear, so the event suggests one list and each group works from
          its own copy. Rendering them flat gave no answer to "whose list is this?" — the only
          thing that matters when four patrols are each bringing a stove. */}
      {(groupSuggestions.length > 0 || groupCopies.length > 0) && (
        <section style={{ marginBottom: 'var(--space-4)' }}>
          <h3
            className="t-label"
            style={{ color: 'var(--color-text-muted)', margin: '0 0 var(--space-2)' }}
          >
            What each {subgroupLabelSingular.toLowerCase()} brings
          </h3>

          {groupSuggestions.map((suggestion) => (
            <SharedList
              key={suggestion.id}
              list={suggestion}
              unitId={unitId}
              subtitle={`Suggested for every ${subgroupLabelSingular.toLowerCase()} · each one works from its own copy`}
              assignable={false}
              people={[]}
              canManage={canManage}
              busy={busy}
              onRun={run}
            />
          ))}

          {groupsWithLists.map((group) => {
            const copy = groupCopies.find((l) => l.subgroupId === group.id);

            return (
              copy && (
                <SharedList
                  key={group.id}
                  list={copy}
                  unitId={unitId}
                  subtitle={`${group.name} · ${group.members.length} ${
                    group.members.length === 1 ? 'person' : 'people'
                  }`}
                  // A group assigns its own members. Offering the whole roster here is how the
                  // griddle ends up against somebody in another patrol.
                  people={group.members.map((m) => ({
                    personId: m.personId,
                    displayName: m.displayName,
                  }))}
                  canManage={canManage}
                  busy={busy}
                  onRun={run}
                />
              )
            );
          })}

          {canManage && groupsWithout.length > 0 && (
            <StartForGroup
              groups={groupsWithout}
              subgroupLabelSingular={subgroupLabelSingular}
              busy={busy}
              onStart={(subgroupId) =>
                run(() => packingApi.adoptForSubgroup(unitId, eventId, subgroupId))
              }
            />
          )}

          {canManage && subgroups.length === 0 && (
            <p className="t-caption" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
              This unit has no {subgroupLabelPlural.toLowerCase()} yet.
            </p>
          )}
        </section>
      )}

      {/* The suggested kit, for the officer who curates it. Everybody else reads it as their own
          list below — showing it twice was the bug, and an assignee on personal gear is refused
          by the API anyway, so this card never offers one. */}
      {canManage &&
        personalSuggestions.map((suggestion) => (
          <SharedList
            key={suggestion.id}
            list={suggestion}
            unitId={unitId}
            subtitle="What each person brings · what a Scout starts from"
            assignable={false}
            people={[]}
            canManage={canManage}
            busy={busy}
            onRun={run}
          />
        ))}

      {/* What this kind of event suggests, offered before the manual picker. The point of
          filing lists under a kind is that the leader does not have to remember which list they
          want -- picking Rafting Campout is what makes the rafting lists appear. */}
      {canManage && (offers ?? []).some((o) => !o.alreadyPublished) && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: '0 0 var(--space-2)' }}>
            This kind of event suggests:
          </p>
          <div className="flex flex-wrap gap-2">
            {(offers ?? [])
              .filter((o) => !o.alreadyPublished)
              .map((offer) => (
                <Button
                  key={offer.sourceListId}
                  variant="secondary"
                  disabledReason={busy ? 'One moment.' : undefined}
                  onClick={() =>
                    run(async () => {
                      await packingApi.publish(unitId, eventId, offer.sourceListId);
                      setOffers(await packingApi.offers(unitId, eventId));
                    })
                  }
                >
                  <Plus size={16} aria-hidden="true" />
                  {offer.name} ({offer.itemCount})
                </Button>
              ))}
          </div>
        </div>
      )}

      {canManage && (templates?.length ?? 0) > 0 && (
        <PublishRow
          templates={templates ?? []}
          // Every published row, not just the ones rendered as cards above: the personal
          // suggestion is published too, and leaving it out of this offers it a second time.
          alreadyPublished={published.map((l) => l.sourceListId)}
          subgroupLabelSingular={subgroupLabelSingular}
          busy={busy}
          onPublish={(sourceListId) => run(() => packingApi.publish(unitId, eventId, sourceListId))}
        />
      )}
    </>
  );

  const checklist = (
    <MyKit
      unitId={unitId}
      eventId={eventId}
      mine={mine}
      suggestion={personalSuggestions[0]}
      troopAssignments={troopAssignments}
      groupAssignments={groupAssignments}
      subgroupLabelSingular={subgroupLabelSingular}
      busy={busy}
      onRun={run}
    />
  );

  // Only offer the second tab when it has something in it. A member on an event carrying no shared
  // lists would otherwise get a tab bar whose other half is blank; Tabs renders a lone panel with
  // no bar at all.
  const hasPlan =
    canManage || unitLists.length > 0 || groupSuggestions.length > 0 || groupCopies.length > 0;

  return (
    // No heading: the docket row above is the heading. See FamilyPacking for the same note.
    <div>

      {error && (
        <p
          className="t-caption"
          role="alert"
          style={{ color: 'var(--color-status-blocked)', margin: '0 0 var(--space-3)' }}
        >
          {error}
        </p>
      )}

      {/* The same two words Going and Forms use. This panel had the right shape first and the
          wrong labels for it: "What you're bringing" and "Who's bringing what" are accurate and
          are a different vocabulary in every panel, which is what made three sections that work
          identically feel like three different screens (TROOP-137). */}
      <PanelTabs
        label="What to bring"
        selected={tab}
        onSelect={setTab}
        yours={checklist}
        everyone={hasPlan ? plan : undefined}
      />
    </div>
  );
}


/**
 * The one control that starts a group's copy, however many groups are still without one.
 *
 * A row per group reads fine for four patrols and is unusable for twenty -- and the dev database,
 * carrying eighty-two groups left behind by smoke runs, renders the unusable version. Same trade
 * `ParticipantStrip` makes: bound what is drawn, keep the true count in words.
 */
function StartForGroup({
  groups,
  subgroupLabelSingular,
  busy,
  onStart,
}: {
  groups: { id: string; name: string }[];
  subgroupLabelSingular: string;
  busy: boolean;
  onStart: (subgroupId: string) => Promise<void>;
}) {
  const [choice, setChoice] = useState('');
  const picker = useRef<HTMLSelectElement>(null);

  return (
    <div
      className="flex flex-wrap items-end gap-2"
      style={{
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-4)',
        marginBottom: 'var(--space-3)',
      }}
    >
      <label className="flex flex-col" style={{ gap: 'var(--space-2)', flex: '1 1 14rem' }}>
        <span className="t-label" style={{ color: 'var(--color-text-muted)' }}>
          Start a {subgroupLabelSingular.toLowerCase()}&rsquo;s list
        </span>
        <select
          ref={picker}
          value={choice}
          disabled={busy}
          onChange={(e) => setChoice(e.target.value)}
          style={{
            minHeight: '48px',
            borderRadius: 'var(--radius-md)',
            border: '1.5px solid var(--color-border-strong)',
            background: 'var(--color-surface-raised)',
            color: 'var(--color-text-primary)',
            padding: '0 var(--space-3)',
          }}
        >
          <option value="">{groups.length} without one</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>
      </label>

      {/* Enabled, for the reason the Add button is: the caption a disabled one has to carry sits
          in the button's own column and lifts it off the control beside it. Clicking with nothing
          picked opens the picker, which is where the answer goes. */}
      <Button
        variant="secondary"
        disabledReason={busy ? 'One moment.' : undefined}
        onClick={async () => {
          if (!choice) {
            picker.current?.focus();
            return;
          }

          await onStart(choice);
          setChoice('');
        }}
      >
        <Plus size={16} aria-hidden="true" />
        Start it
      </Button>
    </div>
  );
}

/**
 * A form for putting one more thing on a list.
 *
 * **The Add button is not disabled when the field is empty.** It used to be, with the reason
 * "Say what to add." rendered underneath by `Button` — which restated the empty field an inch to
 * its left and, because the reason renders inside the button's own column, pushed the button above
 * the input it belongs to (TROOP-74). An empty field is its own explanation; clicking Add with
 * nothing typed puts the cursor where the answer goes. The design-system rule is untouched: it
 * governs *disabled* controls, and this one is no longer disabled.
 */
function AddSomething({
  placeholder,
  busy,
  onAdd,
}: {
  placeholder: string;
  busy: boolean;
  onAdd: (text: string) => Promise<void>;
}) {
  const [adding, setAdding] = useState('');
  const field = useRef<HTMLInputElement>(null);

  return (
    <form
      className="flex flex-wrap items-end gap-2"
      style={{ marginTop: 'var(--space-3)' }}
      onSubmit={async (e) => {
        e.preventDefault();

        if (!adding.trim()) {
          field.current?.focus();
          return;
        }

        await onAdd(adding);
        setAdding('');
      }}
    >
      <div style={{ flex: '1 1 12rem' }}>
        <TextField
          ref={field}
          label="Add something"
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
          placeholder={placeholder}
        />
      </div>
      <Button variant="secondary" type="submit" disabledReason={busy ? 'One moment.' : undefined}>
        <Plus size={16} aria-hidden="true" />
        Add
      </Button>
    </form>
  );
}

/**
 * What the unit or one group brings, and who is bringing it.
 *
 * The assignment is the point of this list. `AssignedToPersonId` on a shared list is the direct
 * answer to the question the original problem statement counted 200 messages about — and it is
 * state on the event, so nobody has to scroll for it.
 *
 * `subtitle` is passed rather than derived: the same component renders the unit's gear, one
 * patrol's copy and the suggestion each patrol copies, and those are three different sentences.
 */
function SharedList({
  list,
  unitId,
  subtitle,
  people,
  assignable = true,
  canManage,
  busy,
  onRun,
}: {
  list: PackingList;
  unitId: string;
  subtitle: string;
  /** Who may be down to bring something — the roster, or one group's own members. */
  people: { personId: string; displayName: string }[];
  /** False for a list nobody is assigned on: the suggestion, and personal kit. */
  assignable?: boolean;
  /**
   * Whether this caller holds the office. Deliberately not `list.canWrite`: a member writes their
   * own kit list outright, and a Scout declaring their own sleeping bag into the unit's register is
   * the one direction TROOP-168 refuses. Marking is an officer act, and the server says so too.
   */
  canManage: boolean;
  busy: boolean;
  onRun: (action: () => Promise<unknown>) => Promise<void>;
}) {
  return (
    <section
      style={{
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-4)',
        marginBottom: 'var(--space-3)',
      }}
    >
      <h3 className="t-body-strong" style={{ color: 'var(--color-text-primary)', margin: 0 }}>
        {list.name}
      </h3>
      <p className="t-caption" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
        {subtitle}
        {!list.canWrite && ' · your officers say who brings what'}
      </p>

      <ul style={{ listStyle: 'none', margin: 'var(--space-3) 0 0', padding: 0 }}>
        {list.items.map((item) => (
          <li
            key={item.id}
            className="flex flex-wrap items-center justify-between gap-2"
            style={{ paddingBlock: 'var(--space-2)', borderTop: '1px solid var(--color-border-subtle)' }}
          >
            {/* flex-1 rather than justify-between doing the work. With three items on the row --
                name, the troop's box, remove -- space-between puts the FREE space between them, so
                the checkbox lands at a different x on every row and a column of them reads as
                ragged (seen in prod, TROOP-168). Letting the name absorb the slack packs the two
                controls against the right edge at their own constant widths, which is what makes
                the boxes line up. minWidth: 0 so a long name wraps inside the row rather than
                pushing the controls off it. */}
            {/* flex-1 rather than justify-between doing the work: with three or four things on
                the row, space-between puts the FREE space BETWEEN them, so the troop's box landed
                at a different x on every row and a column of them read as ragged (seen in prod).
                Letting the name absorb the slack packs the controls to the right instead.

                The 6rem floor is not decoration. `minWidth: 0` alone let the name lose the whole
                row to the assignee select -- which sizes to its longest person name -- collapsing
                it to 0px and wrapping the text one character per line into a 215px-tall row. A
                floor plus the wrap above means a full row breaks onto two lines rather than
                crushing anything. */}
            <span
              className="t-body"
              style={{ color: 'var(--color-text-primary)', flex: '1 1 8rem', minWidth: '6rem' }}
            >
              {item.text}
              {item.quantity > 1 && (
                <span className="tabular" style={{ color: 'var(--color-text-muted)' }}>
                  {' '}
                  &times;{item.quantity}
                </span>
              )}
              {!canManage && item.isUnitOwned && (
                <>
                  {' '}
                  <ItemBadge />
                </>
              )}
            </span>

            {/* The officer's control (TROOP-168). Its own flex item rather than inline with the
                text, so a long item name wraps above it instead of pushing it off the row. */}
            {canManage && (
              <UnitOwnedToggle
                itemText={item.text}
                isUnitOwned={item.isUnitOwned}
                busy={busy}
                onChange={(next) =>
                  onRun(() => gearApi.setUnitOwned(unitId, list.id, item.id, next))
                }
              />
            )}

            {assignable && list.canWrite && (
              <label className="inline-flex items-center gap-2" style={{ minWidth: 0 }}>
                <span className="sr-only-focusable t-caption">Who is bringing {item.text}</span>
                <select
                  value={item.assignedToPersonId ?? ''}
                  disabled={busy}
                  onChange={(e) =>
                    onRun(() =>
                      packingApi.assign(unitId, list.id, item.id, e.target.value || null),
                    )
                  }
                  style={{
                    minHeight: 'var(--tap-min)',
                    borderRadius: 'var(--radius-md)',
                    border: '1.5px solid var(--color-border-strong)',
                    background: 'var(--color-surface-raised)',
                    color: 'var(--color-text-primary)',
                    padding: '0 var(--space-2)',
                    // Sizes to its longest person name otherwise, which on a real roster is what
                    // pushes this row past a phone.
                    maxWidth: '100%',
                  }}
                >
                  <option value="">Nobody yet</option>
                  {people.map((p) => (
                    <option key={p.personId} value={p.personId}>
                      {p.displayName}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {assignable && !list.canWrite && (
              <span
                className="t-caption inline-flex items-center gap-1"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                <UserRound size={13} aria-hidden="true" />
                {item.assignedToName ?? 'Nobody yet'}
              </span>
            )}

            {list.canWrite && !assignable && (
              <button
                type="button"
                aria-label={`Take ${item.text} off ${list.name}`}
                title={`Take ${item.text} off ${list.name}`}
                disabled={busy}
                onClick={() => onRun(() => packingApi.removeItem(unitId, list.id, item.id))}
                className="inline-flex items-center justify-center"
                style={{
                  minHeight: 'var(--tap-min)',
                  minWidth: 'var(--tap-min)',
                  background: 'none',
                  border: 'none',
                  color: 'var(--color-text-muted)',
                  cursor: 'pointer',
                }}
              >
                <X size={16} aria-hidden="true" />
              </button>
            )}
          </li>
        ))}
      </ul>

      {list.canWrite && (
        <AddSomething
          placeholder="Dutch oven"
          busy={busy}
          onAdd={(text) => onRun(() => packingApi.addItem(unitId, list.id, { text }))}
        />
      )}
    </section>
  );
}

/** An officer putting one of the unit's saved lists onto this event. A copy, from then on. */
function PublishRow({
  templates,
  alreadyPublished,
  subgroupLabelSingular,
  busy,
  onPublish,
}: {
  templates: PackingList[];
  alreadyPublished: (string | null)[];
  subgroupLabelSingular: string;
  busy: boolean;
  onPublish: (sourceListId: string) => Promise<void>;
}) {
  const [choice, setChoice] = useState('');
  const available = templates.filter((t) => !alreadyPublished.includes(t.id));

  if (available.length === 0) {
    return null;
  }

  return (
    <div
      className="flex flex-wrap items-end gap-2"
      style={{ marginBottom: 'var(--space-4)' }}
    >
      {/* minWidth: 0 is what makes the select's own maxWidth mean anything. A flex item defaults to
          min-width: auto, so this label refuses to shrink below its widest child -- the select
          sized to its longest option -- and `max-width: 100%` on the select then resolves against
          a parent that has already grown to fit it. Both, or neither works. */}
      <label
        className="flex flex-col"
        style={{ gap: 'var(--space-2)', flex: '1 1 14rem', minWidth: 0 }}
      >
        <span className="t-label" style={{ color: 'var(--color-text-muted)' }}>
          Put a saved list on this event
        </span>
        <select
          value={choice}
          disabled={busy}
          onChange={(e) => setChoice(e.target.value)}
          style={{
            minHeight: '48px',
            borderRadius: 'var(--radius-md)',
            border: '1.5px solid var(--color-border-strong)',
            background: 'var(--color-surface-raised)',
            color: 'var(--color-text-primary)',
            padding: '0 var(--space-3)',
            // A select sizes itself to its LONGEST option, so one list named "Summer camp -- what
            // every Scout brings for the week" made this 482px inside a 390px phone and pushed the
            // whole page into a horizontal scroll. The cap costs nothing: the dropdown itself is
            // drawn by the browser at whatever width it needs.
            maxWidth: '100%',
          }}
        >
          <option value="">Pick one</option>
          {available.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} ({levelWords(t.level, subgroupLabelSingular)})
            </option>
          ))}
        </select>
      </label>

      <Button
        variant="secondary"
        disabledReason={choice ? undefined : 'Pick a list first.'}
        onClick={async () => {
          await onPublish(choice);
          setChoice('');
        }}
      >
        Put it on this event
      </Button>
    </div>
  );
}

/** One thing this person has been put down for, and the list it still belongs to. */
interface Assignment {
  listId: string;
  listName: string;
  /** The group whose copy this is, when it is a group's list. */
  groupName: string | null;
  item: PackingItem;
}

/**
 * One row of gear somebody is bringing for the troop or their group.
 *
 * **It ticks, and that took a policy change rather than a prop.** Every row here is assigned to
 * the viewer by construction, and `PackingAccessPolicy.CanCheckItem` says the assignee may tick
 * their own item on any list they can read — so the checkbox is unconditional here without this
 * card holding an opinion of its own about who may write (TROOP-75). It is still only a tick: the
 * row carries no remove and no assignee control, because neither is the assignee's to change.
 */
function AssignedRow({
  assignment,
  unitId,
  busy,
  onRun,
}: {
  assignment: Assignment;
  unitId: string;
  busy: boolean;
  onRun: (action: () => Promise<unknown>) => Promise<void>;
}) {
  const { item, listId, listName, groupName } = assignment;

  const label = (
    <span
      className="t-body"
      style={{
        color: item.isChecked ? 'var(--color-text-muted)' : 'var(--color-text-primary)',
        textDecoration: item.isChecked ? 'line-through' : undefined,
      }}
    >
      {item.text}
      {item.quantity > 1 && (
        <span className="tabular" style={{ color: 'var(--color-text-muted)' }}>
          {' '}
          &times;{item.quantity}
        </span>
      )}
      <span className="t-caption" style={{ color: 'var(--color-text-muted)' }}>
        {' '}
        &middot; {groupName ?? listName}
      </span>
      {item.isUnitOwned && (
        <>
          {' '}
          <ItemBadge />
        </>
      )}
    </span>
  );

  return (
    <li
      className="flex items-center justify-between gap-2"
      style={{ borderTop: '1px solid var(--color-border-subtle)' }}
    >
      <label
        className="flex flex-1 items-center gap-3"
        style={{ paddingBlock: 'var(--space-2)', minHeight: 'var(--tap-min)', cursor: 'pointer' }}
      >
        <input
          type="checkbox"
          checked={item.isChecked}
          disabled={busy}
          onChange={(e) => onRun(() => packingApi.check(unitId, listId, item.id, e.target.checked))}
          style={{ width: 20, height: 20 }}
        />
        {label}
      </label>
    </li>
  );
}

/**
 * What one person is bringing to this event — all of it, in one place.
 *
 * **The three parts answer one question.** A Scout down for the patrol stove and the troop trailer
 * used to have to read three separate cards and hold the answer in their head; what a family asks
 * the night before is "what goes in my car", and that is this card (TROOP-75). The parts stay
 * labelled rather than merged, because who is responsible is the difference between forgetting
 * your own sleeping bag and leaving a patrol without a stove.
 *
 * **Nothing is copied.** The assigned rows are read off the shared lists the event already
 * returned and still live there: a copy would drift the moment an officer moved the griddle, and
 * §8 keeps one home for who-is-bringing-what.
 *
 * Their own part still materialises nothing until they choose where to start, which is §8's answer
 * to N2 — but the assigned parts render regardless, because being down for the stove is exactly
 * the case where nothing of their own exists yet.
 */
function MyKit({
  unitId,
  eventId,
  mine,
  suggestion,
  troopAssignments,
  groupAssignments,
  subgroupLabelSingular,
  busy,
  onRun,
}: {
  unitId: string;
  eventId: string;
  mine: PackingList | null;
  suggestion: PackingList | undefined;
  troopAssignments: Assignment[];
  groupAssignments: Assignment[];
  subgroupLabelSingular: string;
  busy: boolean;
  onRun: (action: () => Promise<unknown>) => Promise<void>;
}) {
  const [own, setOwn] = useState<PackingList[]>([]);

  useEffect(() => {
    // Their own saved lists, from the route that has no unit in it -- they belong to the person and
    // follow them everywhere. Quiet on failure: it only removes one of the three ways to start.
    packingApi.mine().then(setOwn).catch(() => undefined);
  }, []);

  return (
    <section
      style={{
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-4)',
      }}
    >
      <h3 className="t-body-strong" style={{ color: 'var(--color-text-primary)', margin: 0 }}>
        What you&rsquo;re bringing
      </h3>
      <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
        Everything down to you for this one. Your own part is yours and your parents&rsquo;; your
        leaders see how far along you are and not what is on it.
      </p>

      {troopAssignments.length > 0 && (
        <>
          <h4
            className="t-label"
            style={{ color: 'var(--color-text-muted)', margin: 'var(--space-4) 0 0' }}
          >
            For the troop
          </h4>
          <ul style={{ listStyle: 'none', margin: 'var(--space-2) 0 0', padding: 0 }}>
            {troopAssignments.map((a) => (
              <AssignedRow
                key={a.item.id}
                assignment={a}
                unitId={unitId}
                busy={busy}
                onRun={onRun}
              />
            ))}
          </ul>
        </>
      )}

      {groupAssignments.length > 0 && (
        <>
          <h4
            className="t-label"
            style={{ color: 'var(--color-text-muted)', margin: 'var(--space-4) 0 0' }}
          >
            For your {subgroupLabelSingular.toLowerCase()}
          </h4>
          <ul style={{ listStyle: 'none', margin: 'var(--space-2) 0 0', padding: 0 }}>
            {groupAssignments.map((a) => (
              <AssignedRow
                key={a.item.id}
                assignment={a}
                unitId={unitId}
                busy={busy}
                onRun={onRun}
              />
            ))}
          </ul>
        </>
      )}

      <h4
        className="t-label flex flex-wrap items-baseline gap-2"
        style={{ color: 'var(--color-text-muted)', margin: 'var(--space-4) 0 0' }}
      >
        Yours
        {mine && (
          // Deliberately about their own list only: it is what the leader's progress endpoint
          // counts, and folding in gear they may not even be allowed to tick would make the two
          // numbers disagree.
          <span className="t-caption tabular" style={{ textTransform: 'none', letterSpacing: 0 }}>
            {mine.checkedCount} of {mine.items.length} sorted
          </span>
        )}
      </h4>

      {!mine ? (
        <div className="flex flex-wrap gap-2" style={{ marginTop: 'var(--space-3)' }}>
          {suggestion && (
            <Button
              variant="primary"
              onClick={() => onRun(() => packingApi.startMyList(unitId, eventId, 'EventSuggestion'))}
            >
              Use the unit&rsquo;s list
            </Button>
          )}

          {own.map((t) => (
            <Button
              key={t.id}
              variant="secondary"
              onClick={() =>
                onRun(() => packingApi.startMyList(unitId, eventId, 'MyTemplate', t.id))
              }
            >
              Use &ldquo;{t.name}&rdquo;
            </Button>
          ))}

          <Button
            variant="secondary"
            onClick={() => onRun(() => packingApi.startMyList(unitId, eventId, 'Blank'))}
          >
            Start blank
          </Button>
        </div>
      ) : (
        <>
          <ul style={{ listStyle: 'none', margin: 'var(--space-2) 0 0', padding: 0 }}>
            {mine.items.map((item) => (
              <li
                key={item.id}
                className="flex items-center justify-between gap-2"
                style={{ borderTop: '1px solid var(--color-border-subtle)' }}
              >
                <label
                  className="flex flex-1 items-center gap-3"
                  style={{
                    paddingBlock: 'var(--space-2)',
                    minHeight: 'var(--tap-min)',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={item.isChecked}
                    disabled={busy}
                    onChange={(e) =>
                      onRun(() => packingApi.check(unitId, mine.id, item.id, e.target.checked))
                    }
                    style={{ width: 20, height: 20 }}
                  />
                  <span
                    className="t-body"
                    style={{
                      color: item.isChecked
                        ? 'var(--color-text-muted)'
                        : 'var(--color-text-primary)',
                      textDecoration: item.isChecked ? 'line-through' : undefined,
                    }}
                  >
                    {item.text}
                    {item.isOptional && (
                      <span className="t-caption" style={{ color: 'var(--color-text-muted)' }}>
                        {' '}
                        (optional)
                      </span>
                    )}
                    {item.isUnitOwned && (
                      <>
                        {' '}
                        <ItemBadge />
                      </>
                    )}
                  </span>
                </label>

                <button
                  type="button"
                  aria-label={`Take ${item.text} off my list`}
                  title={`Take ${item.text} off my list`}
                  disabled={busy}
                  onClick={() => onRun(() => packingApi.removeItem(unitId, mine.id, item.id))}
                  className="inline-flex items-center justify-center"
                  style={{
                    minHeight: 'var(--tap-min)',
                    minWidth: 'var(--tap-min)',
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-text-muted)',
                    cursor: 'pointer',
                  }}
                >
                  <X size={16} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>

          <AddSomething
            placeholder="Book"
            busy={busy}
            onAdd={(text) => onRun(() => packingApi.addItem(unitId, mine.id, { text }))}
          />

          {mine.items.length > 0 && (
            <p style={{ marginTop: 'var(--space-3)' }}>
              <button
                type="button"
                className="link-button t-caption"
                disabled={busy}
                onClick={() =>
                  onRun(() =>
                    packingApi.saveAsTemplate(unitId, mine.id, {
                      name: 'My usual list',
                      owner: 'me',
                    }),
                  )
                }
              >
                <span className="inline-flex items-center gap-2">
                  <Check size={13} aria-hidden="true" />
                  Keep this list for next time
                </span>
              </button>
            </p>
          )}
        </>
      )}
    </section>
  );
}
